import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Stack, Box } from '@mui/material';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  Scatter,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ColorToken } from '../../../style/types';
import { getColor, radius } from '../../../style/utils';
import { ModelRun } from '../../../types';
import { isInSaaSMode } from '../../../utils/common';
import { formatShortDateTime, localizeDate } from '../../../utils/datetime';
import { colorTokens, themeTokens } from '../../../style/tokens';
import RunsChartTooltip from './ModelRunsChartTooltip';

const barColorMap: Record<string, ColorToken> = {
  error: 'status.error.bright',
  success: 'status.success.bright',
};

const barHoverColorMap = {
  error: colorTokens.status.error.fill,
  success: colorTokens.status.success.fill,
} as any;

const Wrapper = styled(Stack)`
  border-radius: ${radius('lg')};
  background-color: ${({ theme }) => theme.palette.surface.subtle};
  width: 100%;
`;

const DotWrapper = styled(Stack)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const DotElement = styled(Box)<{ status: string }>`
  height: 8px;
  width: 8px;
  background: ${({ theme, status }) => getColor(theme, barColorMap[status])};
  border: 1px solid transparent;
  border-radius: ${radius('sm')};
  &:hover {
    border: ${({ status }) => `3px solid ${barHoverColorMap[status]}`};
    box-sizing: content-box;
    border-radius: ${radius('lg')};
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
  }
`;

const renderTooltip = (props: any) => <RunsChartTooltip {...props} />;

const ChartDot = ({ cx, cy, payload }: any) => (
  <foreignObject x={cx - 8} y={cy - 8} height={16} width={16}>
    <DotWrapper>
      <DotElement status={payload.status} />
    </DotWrapper>
  </foreignObject>
);

const ModelRunsChart = ({ data }: { data: ModelRun[] }) => {
  const scrollContainerRef = useRef<any>();

  const normalizedData = useMemo(
    () =>
      Array.isArray(data)
        ? [...data]
            .sort(
              (a, b) =>
                dayjs(a.time_utc).valueOf() - dayjs(b.time_utc).valueOf(),
            )
            .map((item) => ({
              ...item,
              execution_time: item.execution_time || 0,
            }))
        : [],
    [data],
  );

  const getErrorExecTime = useCallback(
    (index: number) => {
      const prevValue = normalizedData[index - 1]?.execution_time || 0;
      const nextValue = normalizedData[index + 1]?.execution_time || 0;
      return (prevValue + nextValue) / 2;
    },
    [normalizedData],
  );

  const scatterLineData = useMemo(
    () =>
      normalizedData.map((item, index: number) =>
        item.status === 'error'
          ? { ...item, execution_time: getErrorExecTime(index) }
          : item,
      ),
    [getErrorExecTime, normalizedData],
  );

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current?.scrollWidth;
    }
  }, []);

  return (
    <Wrapper
      direction="row"
      aria-label={isInSaaSMode() ? 'Task runs chart' : 'Model runs chart'}
    >
      <ComposedChart
        data={normalizedData}
        margin={{ bottom: 46, top: 30, left: 14 }}
        height={300}
        width={80}
      >
        <YAxis
          axisLine={false}
          tickLine={false}
          tickMargin={6}
          dataKey="execution_time"
          tick={{ fontSize: themeTokens.fontSize.md }}
          unit="s"
          tickFormatter={(value) => Humanize.compactInteger(value)}
        />
      </ComposedChart>

      <Box
        sx={{ overflowX: 'auto', overflowY: 'hidden', flex: 1 }}
        ref={scrollContainerRef}
      >
        <ResponsiveContainer
          minWidth={normalizedData.length * 40}
          width="99%"
          height={300}
        >
          <ComposedChart
            data={normalizedData}
            margin={{ top: 28, right: 20, bottom: 14 }}
          >
            <CartesianGrid stroke={colorTokens.chart.gridLine} />

            <Scatter
              data={scatterLineData}
              shape={() => <div />}
              line={{
                stroke: colorTokens.accent.link,
                strokeDasharray: 4,
              }}
              lineType="fitting"
            />

            <XAxis
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              padding={{ left: 50 }}
              tickFormatter={(value) =>
                dayjs(value).isValid() && value !== 0
                  ? formatShortDateTime(localizeDate(value))
                  : ''
              }
              dataKey="time_utc"
              tick={{ fontSize: themeTokens.fontSize.xs }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              dataKey="execution_time"
              tick={{ fontSize: themeTokens.fontSize.md }}
              hide
              tickFormatter={(value) => Humanize.compactInteger(value)}
            />

            <Tooltip
              content={(props: any) => renderTooltip(props)}
              cursor
              isAnimationActive
              wrapperStyle={{
                outline: 0,
              }}
            />

            <Line
              dataKey="execution_time"
              stroke="none"
              dot={<ChartDot />}
              activeDot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Wrapper>
  );
};

export default ModelRunsChart;
